/* Petits composants d'interface partagés par le CRM. */
import React, { useState } from 'react';
import { X } from 'lucide-react';

export const inputClass =
  'w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50 bg-white disabled:bg-neutral-50 disabled:text-neutral-400';

export function Field({
  label,
  required,
  missing,
  error,
  vars,
  children,
  className = '',
}: {
  label: string;
  /** Variables des modèles d'e-mail remplies par ce champ, ex. {{prenom}}. */
  vars?: { key: string; label: string }[];
  required?: boolean;
  missing?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    // Libellés sur une ou deux lignes : les champs d'une même rangée restent alignés (colonne calée en bas).
    <label className={`flex flex-col justify-end ${className}`}>
      <span className={`block text-[11px] font-medium mb-1 ${missing ? 'text-amber-700' : 'text-neutral-600'}`}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
        {missing && <span className="ml-1.5 text-[10px] font-semibold text-amber-600">à compléter</span>}
        {vars?.map(v => (
          <code key={v.key} title={`Variable des modèles d’e-mail : ${v.label}`}
            className="ml-1.5 px-1 py-px rounded bg-neutral-100 text-[10px] font-mono font-normal text-neutral-500 whitespace-nowrap">{v.key}</code>
        ))}
      </span>
      {children}
      {error && <span className="block text-[11px] text-red-600 mt-1">{error}</span>}
    </label>
  );
}

/** Classe de bordure d'un champ selon son état. */
export function borderFor(missing?: boolean, error?: string): string {
  if (error) return 'border-red-300';
  if (missing) return 'border-amber-400 bg-amber-50/40';
  return 'border-neutral-200';
}

export function StatusDot({ on, label, pending = false, title }: { on: boolean; label: string; pending?: boolean; title?: string }) {
  const color = on ? 'bg-emerald-500' : pending ? 'bg-amber-400' : 'bg-neutral-300';
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-neutral-600" title={title ?? label}>
      <span className={`w-2 h-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}

export function ModalShell({
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 'max-w-md',
  zIndex = 'z-[70]',
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
  zIndex?: string;
}) {
  return (
    <div className={`fixed inset-0 ${zIndex} flex items-center justify-center p-4`}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${width} flex flex-col overflow-hidden`} style={{ maxHeight: '90vh' }}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-neutral-100">
          <div>
            <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
            {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-neutral-100 bg-neutral-50/60 flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
}

export function formatDateFr(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('fr-BE');
}

/**
 * Champ texte avec suggestions (même comportement que la fenêtre de création d'un participant) :
 * la liste s'ouvre dès la frappe et propose les valeurs existantes qui contiennent le texte saisi.
 */
export function SuggestInput({
  value,
  onChange,
  suggestions,
  placeholder,
  className = '',
  disabled = false,
  icon,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: { value: string; hint?: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const q = value.trim().toLowerCase();
  const matches = q ? suggestions.filter(s => s.value.toLowerCase().includes(q) && s.value.toLowerCase() !== q).slice(0, 8) : [];
  return (
    <div className="relative">
      <input
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={`${inputClass} ${className}`}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {matches.map(m => (
            <button
              key={m.value}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onChange(m.value); setOpen(false); }}
              className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 text-sm hover:bg-amber-50 border-b border-neutral-100 last:border-b-0"
            >
              <span className="flex items-center gap-2 text-neutral-800">{icon}{m.value}</span>
              {m.hint && <span className="text-[10px] text-neutral-400 shrink-0">{m.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Message lisible d'une erreur : les erreurs Supabase sont des objets { message, details, hint, code },
 * pas des Error (sans ce traitement, elles s'affichent « [object Object] »).
 */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object') {
    const e = err as { message?: string; details?: string; hint?: string; code?: string; error?: string };
    const text = [e.message ?? e.error, e.details, e.hint].filter(Boolean).join(' · ');
    if (text) return e.code ? `${text} (${e.code})` : text;
    try { return JSON.stringify(err); } catch { /* objet circulaire */ }
  }
  return String(err);
}
